import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router';
import format from 'date-fns/format';
import { useSelector, useDispatch } from 'react-redux';
import { styled } from '@mui/material/styles';
import RefreshIcon from '@mui/icons-material/Refresh';
import CircularProgress from '@mui/material/CircularProgress';
import { useTranslation, useQueryParams, useSyncProfilesFilters, usePermissions } from '@hooks';
import PageHeader from '@features/PageHeader';
import ExportCSV from '@features/ExportCSV';
import Button from '@components/Button';
import { profilesService } from '@services';
import { profilesActions, alertActions } from '@actions';
import { AccIntegrationName } from '@constants/integrations';
import { isIntegrationConnected } from '@helpers/integrations';
import { isDirectoryPlatformId } from '@helpers/directories';
import { ExternalRoutes, RoutePath } from '@constants/routes';
import { SYNC_VIEW } from '@constants/profiles';
import { DEFAULT_PAGE_SIZE } from '@constants/pagination';
import { handleApiError } from '@helpers/api';
import PageContainer from '@components/Layout/PageContainer';
import { IntegrationRequiredIcon } from 'assets/images/icons';
import FiltersPanel from './FiltersPanel';
import ProfilesTable from './ProfilesTable';
import ListingIssuesBanner from './ListingIssuesBanner';
import StatusChart from './StatusChart';
import ResolveModal from './ResolveModal';

const Container = styled(PageContainer)({
    '& .page-header-controls': {
        width: '100%',
    },
});

export const StyledFiltersPanel = styled(FiltersPanel)({
    '& svg': {
        height: 'auto',
        width: 'auto',
    },
});

const StyledSyncButton = styled(Button)({
    '& svg': {
        height: 'auto',
        width: 'auto',
    },
});

const DEFAULT_VIEWS = [
    SYNC_VIEW.DISABLED,
    SYNC_VIEW.IN_PROGRESS,
    SYNC_VIEW.CONFLICT,
    SYNC_VIEW.NOT_PUBLISHED,
    SYNC_VIEW.SYNCED,
];

const VALID_VIEW_VALUES = new Set(Object.values(SYNC_VIEW));

const sanitizeViews = parsed => {
    if (!Array.isArray(parsed)) return null;
    const cleaned = Array.from(new Set(parsed.filter(v => VALID_VIEW_VALUES.has(v))));
    return cleaned.length > 0 ? cleaned : null;
};

const parseViewsFromQuery = params => {
    const rawView = params.get('view');
    if (!rawView) return DEFAULT_VIEWS;
    try {
        return sanitizeViews(JSON.parse(rawView)) ?? DEFAULT_VIEWS;
    } catch {
        return DEFAULT_VIEWS;
    }
};

const ProfilesPage = () => {
    const { t } = useTranslation();
    const params = useQueryParams();
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_PAGE_SIZE);
    const [filter, setFilter] = useState({
        companies: [],
        platforms: [],
        views: parseViewsFromQuery(params),
    });
    const [filterNumber, setFilterNumber] = useState(0);
    const [filterOpened, setFiltersOpened] = useState(false);
    const [resolveModalOpened, setResolveModalOpened] = useState(false);
    const [selectedProfileId, setSelectedProfileId] = useState(null);

    const scopedLocationId = useMemo(() => {
        const raw = Number(params.get('locationId'));
        return Number.isInteger(raw) && raw > 0 ? raw : null;
    }, [params]);
    const directoriesOnly = params.get('type') === 'directories';

    const account = useSelector(state => state.account?.account);
    const companies = useSelector(state => state.account?.account?.companies || []);
    const { syncCompleted } = useSelector(state => state.profiles);
    const user = useSelector(state => state.auth.user);

    const { locationsValues, getPlatformsValues } = useSyncProfilesFilters();
    const { hasFullSettings } = usePermissions();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const firstRender = useRef(true);

    useEffect(() => {
        dispatch(profilesActions.getFilterData());
    }, [dispatch]);

    const GOOGLE_CONNECTED = isIntegrationConnected(account, AccIntegrationName.Google);

    useEffect(() => {
        if (!locationsValues.length) return;

        const scopedCompany = scopedLocationId ? locationsValues.filter(c => c.id === scopedLocationId) : [];

        if (scopedCompany.length) {
            const scopedPlatforms = getPlatformsValues([scopedLocationId]);
            const platforms = directoriesOnly ? scopedPlatforms.filter(isDirectoryPlatformId) : scopedPlatforms;
            setFilter(prev => ({ ...prev, companies: scopedCompany, platforms }));
            return;
        }

        setFilter(prev => ({ ...prev, companies: locationsValues, platforms: getPlatformsValues() }));
    }, [locationsValues]);

    const currentQuery = useMemo(() => {
        return {
            locations: filter.companies.map(c => c.id),
            platforms: filter.platforms.filter(p => Number.isInteger(p)),
            listingDirectoryIds: filter.platforms.filter(isDirectoryPlatformId),
            views: filter.views,
        };
    }, [filter]);

    useEffect(() => {
        if (filter.companies.length) {
            if (firstRender.current) {
                dispatch(profilesActions.get(currentQuery, page, rowsPerPage));
                firstRender.current = false;
            }
        }
    }, [page, filter, currentQuery, rowsPerPage, dispatch]);

    useEffect(() => {
        let interval;

        if (!syncCompleted) {
            interval = setInterval(() => {
                // Silent so the skeleton doesn't flash on every poll tick.
                dispatch(profilesActions.getSilent(currentQuery, page, rowsPerPage));
            }, 10000);
        }

        return () => clearInterval(interval);
    }, [syncCompleted, currentQuery, page, rowsPerPage, dispatch]);

    const onStartSync = async () => {
        try {
            const response = await profilesService.startSync();
            if (response) {
                dispatch(profilesActions.setSyncStatus(response));
                dispatch(alertActions.success(t('ProfilesPage.syncSuccessMessage')));
            }
        } catch (error) {
            handleApiError(error);
        }
    };

    const onPageChange = (_, newPage) => {
        setPage(newPage);
        dispatch(profilesActions.get(currentQuery, newPage, rowsPerPage));
    };

    const onRowsPerPageChange = event => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
        dispatch(profilesActions.get(currentQuery, 0, parseInt(event.target.value, 10)));
    };

    const onApplyFilters = () => {
        dispatch(profilesActions.get(currentQuery, 0, rowsPerPage));
        setPage(0);
    };

    const onRefetch = () => dispatch(profilesActions.get(currentQuery, page, rowsPerPage));

    const onClickResolve = profileId => {
        setSelectedProfileId(profileId);
        setResolveModalOpened(true);
    };

    const canManageSettings = hasFullSettings();

    const headerText = !GOOGLE_CONNECTED
        ? { title: t('ProfilesPage.titleGoogle'), subtitle: t('ProfilesPage.subtitleGoogle') }
        : { title: t('ProfilesPage.title'), subtitle: t('ProfilesPage.subtitle') };

    const onClickLearnMore = () => {
        window.open(ExternalRoutes.IntegrationLearnMore[user?.language], '_blank');
    };

    return (
        <Container>
            <PageHeader
                icon={!GOOGLE_CONNECTED ? <IntegrationRequiredIcon /> : <StatusChart />}
                title={headerText.title}
                subtitle={headerText.subtitle}
                styles={{ marginBottom: '16px' }}
            >
                {!GOOGLE_CONNECTED && (
                    <>
                        {canManageSettings ? (
                            <Button variant="contained" onClick={() => navigate(RoutePath.AccountSettingsIntegrations)}>
                                {t('buttons.connect')}
                            </Button>
                        ) : null}
                        <Button variant="outlined" onClick={onClickLearnMore}>
                            {t('common.learnMore')}
                        </Button>
                    </>
                )}
                {GOOGLE_CONNECTED && (
                    <>
                        {syncCompleted ? (
                            <StyledSyncButton variant="contained" onClick={onStartSync}>
                                <RefreshIcon
                                    sx={{
                                        color: '#fff',
                                        width: '1em !important',
                                        height: '1em !important',
                                        fontSize: '22px',
                                    }}
                                />
                                <span style={{ marginLeft: '6px' }}>{t('buttons.startSync')}</span>
                            </StyledSyncButton>
                        ) : (
                            <StyledSyncButton variant="contained" disabled>
                                <CircularProgress sx={{ color: 'var(--text-tertiary)' }} size={16} />
                                <span style={{ marginLeft: '8px' }}>{t('buttons.syncing')}</span>
                            </StyledSyncButton>
                        )}
                        <StyledFiltersPanel
                            setFiltersOpened={setFiltersOpened}
                            filtersNumber={filterNumber}
                            filterOpened={filterOpened}
                            filter={filter}
                            setFilter={setFilter}
                            type="profiles"
                            setFilterNumber={setFilterNumber}
                            onApplyFilters={onApplyFilters}
                            withChips={false}
                        />
                        <ExportCSV
                            callback={() => profilesService.exportCSV(currentQuery)}
                            type={`profiles-${format(new Date(), 'yyyy-MM-dd')}`}
                        />
                    </>
                )}
            </PageHeader>
            {GOOGLE_CONNECTED ? <ListingIssuesBanner /> : null}
            <ProfilesTable
                locations={companies}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
                onClickResolve={onClickResolve}
                onRefetch={onRefetch}
            />

            {resolveModalOpened && (
                <ResolveModal
                    isOpen={resolveModalOpened}
                    handleClose={() => setResolveModalOpened(false)}
                    profileId={selectedProfileId}
                    onRefetchData={onApplyFilters}
                />
            )}
        </Container>
    );
};

export default ProfilesPage;
